import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  GenerateOrgsBillingDataReportParams,
  GenerateProjectBillingDataReportParams,
  GetOrganizationBillingDataParams,
  GetOrganizationBillingDataResponse,
  GetProjectBillingBackupsLicensesDataParams,
  GetProjectBillingBackupsLicensesDataResponse,
  GetProjectBillingBackupsStorageDataParams,
  GetProjectBillingBackupsStorageDataResponse,
  GetProjectBillingDataParams,
  GetProjectBillingDataResponse,
  GetProjectBillingDatabasesDataParams,
  GetProjectBillingDatabasesDataResponse,
  GetProjectBillingInstancesDataParams,
  GetProjectBillingInstancesDataResponse,
  GetProjectBillingS3DataParams,
  GetProjectBillingS3DataResponse,
  GetProjectBillingVolumesDataParams,
  GetProjectBillingVolumesDataResponse,
  GetUserBillingDataParams,
  GetUserBillingDataResponse,
  OrganizationBillingData,
  ProjectBillingBackupsLicensesData,
  ProjectBillingBackupsStorageData,
  ProjectBillingData,
  ProjectBillingDatabasesData,
  ProjectBillingInstancesData,
  ProjectBillingS3Data,
  ProjectBillingVolumesData,
  UpdateProjectBudgetLimitParams,
  UpdateProjectBudgetLimitResponse,
  UserBillingData
} from "./types";

export type BillingState = {
  userBillingData: UserBillingData | null;
  isUserBillingDataLoading: boolean;
  organizationBillingData: OrganizationBillingData | null;
  isOrganizationBillingDataLoading: boolean;
  projectBillingData: ProjectBillingData | null;
  isProjectBillingDataLoading: boolean;
  projectBillingInstancesData: ProjectBillingInstancesData | null;
  isProjectBillingInstancesDataLoading: boolean;
  projectBillingVolumesData: ProjectBillingVolumesData | null;
  isProjectBillingVolumesDataLoading: boolean;
  projectBillingDatabasesData: ProjectBillingDatabasesData | null;
  isProjectBillingDatabasesDataLoading: boolean;
  projectBillingS3Data: ProjectBillingS3Data | null;
  isProjectBillingS3DataLoading: boolean;
  projectBillingBackupsStorageData: ProjectBillingBackupsStorageData | null;
  projectBillingBackupsLicensesData: ProjectBillingBackupsLicensesData | null;
  isProjectBillingBackupsStorageDataLoading: boolean;
  isProjectBillingBackupsLicensesDataLoading: boolean;
  isProjectBillingDataReportGenerating: boolean;
  isOrgsBillingDataReportGenerating: boolean;
  isProjectBudgetLimitUpdating: boolean;
};

const initialState: BillingState = {
  userBillingData: null,
  isUserBillingDataLoading: false,
  organizationBillingData: null,
  isOrganizationBillingDataLoading: false,
  projectBillingData: null,
  isProjectBillingDataLoading: false,
  projectBillingInstancesData: null,
  isProjectBillingInstancesDataLoading: false,
  projectBillingVolumesData: null,
  isProjectBillingVolumesDataLoading: false,
  projectBillingDatabasesData: null,
  isProjectBillingDatabasesDataLoading: false,
  projectBillingS3Data: null,
  isProjectBillingS3DataLoading: false,
  projectBillingBackupsStorageData: null,
  projectBillingBackupsLicensesData: null,
  isProjectBillingBackupsStorageDataLoading: false,
  isProjectBillingBackupsLicensesDataLoading: false,
  isProjectBillingDataReportGenerating: false,
  isOrgsBillingDataReportGenerating: false,
  isProjectBudgetLimitUpdating: false
};

const billingSlice = createSlice({
  name: "billing",
  initialState,
  reducers: {
    // Get User Billing Data
    getUserBillingDataRequest: (
      state,
      action: PayloadAction<GetUserBillingDataParams>
    ) => {
      state.isUserBillingDataLoading = true;
    },
    getUserBillingDataSuccess: (
      state,
      action: PayloadAction<GetUserBillingDataResponse>
    ) => {
      state.userBillingData = action.payload;
      state.isUserBillingDataLoading = false;
    },
    getUserBillingDataFailure: (
      state,
      action: PayloadAction<{ params: GetUserBillingDataParams; error: string }>
    ) => {
      state.isUserBillingDataLoading = false;
    },

    // Get Organization Billing Data
    getOrganizationBillingDataRequest: (
      state,
      action: PayloadAction<GetOrganizationBillingDataParams>
    ) => {
      state.isOrganizationBillingDataLoading = true;
    },
    getOrganizationBillingDataSuccess: (
      state,
      action: PayloadAction<GetOrganizationBillingDataResponse>
    ) => {
      state.organizationBillingData = action.payload;
      state.isOrganizationBillingDataLoading = false;
    },
    getOrganizationBillingDataFailure: (
      state,
      action: PayloadAction<{
        params: GetOrganizationBillingDataParams;
        error: string;
      }>
    ) => {
      state.isOrganizationBillingDataLoading = false;
    },

    // Get Project Billing Data
    getProjectBillingDataRequest: (
      state,
      action: PayloadAction<GetProjectBillingDataParams>
    ) => {
      state.isProjectBillingDataLoading = true;
    },
    getProjectBillingDataSuccess: (
      state,
      action: PayloadAction<GetProjectBillingDataResponse>
    ) => {
      state.projectBillingData = action.payload;
      state.isProjectBillingDataLoading = false;
    },
    getProjectBillingDataFailure: (
      state,
      action: PayloadAction<{
        params: GetProjectBillingDataParams;
        error: string;
      }>
    ) => {
      state.isProjectBillingDataLoading = false;
    },

    // Get Project Billing Instances Data
    getProjectBillingInstancesDataRequest: (
      state,
      action: PayloadAction<GetProjectBillingInstancesDataParams>
    ) => {
      state.isProjectBillingInstancesDataLoading = true;
    },
    getProjectBillingInstancesDataSuccess: (
      state,
      action: PayloadAction<GetProjectBillingInstancesDataResponse>
    ) => {
      state.projectBillingInstancesData = action.payload;
      state.isProjectBillingInstancesDataLoading = false;
    },
    getProjectBillingInstancesDataFailure: (
      state,
      action: PayloadAction<{
        params: GetProjectBillingInstancesDataParams;
        error: string;
      }>
    ) => {
      state.isProjectBillingInstancesDataLoading = false;
    },

    // Get Project Billing Volumes Data
    getProjectBillingVolumesDataRequest: (
      state,
      action: PayloadAction<GetProjectBillingVolumesDataParams>
    ) => {
      state.isProjectBillingVolumesDataLoading = true;
    },
    getProjectBillingVolumesDataSuccess: (
      state,
      action: PayloadAction<GetProjectBillingVolumesDataResponse>
    ) => {
      state.projectBillingVolumesData = action.payload;
      state.isProjectBillingVolumesDataLoading = false;
    },
    getProjectBillingVolumesDataFailure: (
      state,
      action: PayloadAction<{
        params: GetProjectBillingVolumesDataParams;
        error: string;
      }>
    ) => {
      state.isProjectBillingVolumesDataLoading = false;
    },

    // Get Project Billing Databases Data
    getProjectBillingDatabasesDataRequest: (
      state,
      action: PayloadAction<GetProjectBillingDatabasesDataParams>
    ) => {
      state.isProjectBillingDatabasesDataLoading = true;
    },
    getProjectBillingDatabasesDataSuccess: (
      state,
      action: PayloadAction<GetProjectBillingDatabasesDataResponse>
    ) => {
      state.projectBillingDatabasesData = action.payload;
      state.isProjectBillingDatabasesDataLoading = false;
    },
    getProjectBillingDatabasesDataFailure: (
      state,
      action: PayloadAction<{
        params: GetProjectBillingDatabasesDataParams;
        error: string;
      }>
    ) => {
      state.isProjectBillingDatabasesDataLoading = false;
    },

    // Get Project Billing S3 Data
    getProjectBillingS3DataRequest: (
      state,
      action: PayloadAction<GetProjectBillingS3DataParams>
    ) => {
      state.isProjectBillingS3DataLoading = true;
    },
    getProjectBillingS3DataSuccess: (
      state,
      action: PayloadAction<GetProjectBillingS3DataResponse>
    ) => {
      state.projectBillingS3Data = action.payload;
      state.isProjectBillingS3DataLoading = false;
    },
    getProjectBillingS3DataFailure: (
      state,
      action: PayloadAction<{
        params: GetProjectBillingS3DataParams;
        error: string;
      }>
    ) => {
      state.isProjectBillingS3DataLoading = false;
    },

    // Get Project Billing Backups Storage Data
    getProjectBillingBackupsStorageDataRequest: (
      state,
      action: PayloadAction<GetProjectBillingBackupsStorageDataParams>
    ) => {
      state.isProjectBillingBackupsStorageDataLoading = true;
    },
    getProjectBillingBackupsStorageDataSuccess: (
      state,
      action: PayloadAction<GetProjectBillingBackupsStorageDataResponse>
    ) => {
      state.projectBillingBackupsStorageData = action.payload;
      state.isProjectBillingBackupsStorageDataLoading = false;
    },
    getProjectBillingBackupsStorageDataFailure: (
      state,
      action: PayloadAction<{
        params: GetProjectBillingBackupsStorageDataParams;
        error: string;
      }>
    ) => {
      state.isProjectBillingBackupsStorageDataLoading = false;
    },

    // Get Project Billing Backups Licenses Data
    getProjectBillingBackupsLicensesDataRequest: (
      state,
      action: PayloadAction<GetProjectBillingBackupsLicensesDataParams>
    ) => {
      state.isProjectBillingBackupsLicensesDataLoading = true;
    },
    getProjectBillingBackupsLicensesDataSuccess: (
      state,
      action: PayloadAction<GetProjectBillingBackupsLicensesDataResponse>
    ) => {
      state.projectBillingBackupsLicensesData = action.payload;
      state.isProjectBillingBackupsLicensesDataLoading = false;
    },
    getProjectBillingBackupsLicensesDataFailure: (
      state,
      action: PayloadAction<{
        params: GetProjectBillingBackupsLicensesDataParams;
        error: string;
      }>
    ) => {
      state.isProjectBillingBackupsLicensesDataLoading = false;
    },

    // Generate Project Billing Data Report
    generateProjectBillingDataReportRequest: (
      state,
      action: PayloadAction<GenerateProjectBillingDataReportParams>
    ) => {
      state.isProjectBillingDataReportGenerating = true;
    },
    generateProjectBillingDataReportSuccess: (state) => {
      state.isProjectBillingDataReportGenerating = false;
    },
    generateProjectBillingDataReportFailure: (
      state,
      action: PayloadAction<{
        params: GenerateProjectBillingDataReportParams;
        error: string;
      }>
    ) => {
      state.isProjectBillingDataReportGenerating = false;
    },

    // Generate Orgs Billing Data Report
    generateOrgsBillingDataReportRequest: (
      state,
      action: PayloadAction<GenerateOrgsBillingDataReportParams>
    ) => {
      state.isOrgsBillingDataReportGenerating = true;
    },
    generateOrgsBillingDataReportSuccess: (state) => {
      state.isOrgsBillingDataReportGenerating = false;
    },
    generateOrgsBillingDataReportFailure: (
      state,
      action: PayloadAction<{
        params: GenerateOrgsBillingDataReportParams;
        error: string;
      }>
    ) => {
      state.isOrgsBillingDataReportGenerating = false;
    },

    // Update Project Budget Limit
    updateProjectBudgetLimitRequest: (
      state,
      action: PayloadAction<UpdateProjectBudgetLimitParams>
    ) => {
      state.isProjectBudgetLimitUpdating = true;
    },
    updateProjectBudgetLimitSuccess: (
      state,
      action: PayloadAction<UpdateProjectBudgetLimitResponse>
    ) => {
      state.isProjectBudgetLimitUpdating = false;
    },
    updateProjectBudgetLimitFailure: (
      state,
      action: PayloadAction<{
        params: UpdateProjectBudgetLimitParams;
        error: string;
      }>
    ) => {
      state.isProjectBudgetLimitUpdating = false;
    },

    // Clear
    clear: () => initialState
  }
});

export const {
  getUserBillingDataRequest,
  getUserBillingDataSuccess,
  getUserBillingDataFailure,
  getOrganizationBillingDataRequest,
  getOrganizationBillingDataSuccess,
  getOrganizationBillingDataFailure,
  getProjectBillingDataRequest,
  getProjectBillingDataSuccess,
  getProjectBillingDataFailure,
  getProjectBillingInstancesDataRequest,
  getProjectBillingInstancesDataSuccess,
  getProjectBillingInstancesDataFailure,
  getProjectBillingVolumesDataRequest,
  getProjectBillingVolumesDataSuccess,
  getProjectBillingVolumesDataFailure,
  getProjectBillingDatabasesDataRequest,
  getProjectBillingDatabasesDataSuccess,
  getProjectBillingDatabasesDataFailure,
  getProjectBillingS3DataRequest,
  getProjectBillingS3DataSuccess,
  getProjectBillingS3DataFailure,
  getProjectBillingBackupsStorageDataRequest,
  getProjectBillingBackupsStorageDataSuccess,
  getProjectBillingBackupsStorageDataFailure,
  getProjectBillingBackupsLicensesDataRequest,
  getProjectBillingBackupsLicensesDataSuccess,
  getProjectBillingBackupsLicensesDataFailure,
  generateProjectBillingDataReportRequest,
  generateProjectBillingDataReportSuccess,
  generateProjectBillingDataReportFailure,
  generateOrgsBillingDataReportRequest,
  generateOrgsBillingDataReportSuccess,
  generateOrgsBillingDataReportFailure,
  updateProjectBudgetLimitRequest,
  updateProjectBudgetLimitSuccess,
  updateProjectBudgetLimitFailure,
  clear
} = billingSlice.actions;

export const { actions: billingActions } = billingSlice;

export const reducer = billingSlice.reducer;
